import {useEffect, useState} from "react";

export function formatTranslationsToIntl(data) {
    if (!data) {
        return null
    }
    let intlObj = {}
    for (let i = 0; i < data.length; i++) {
        const langObj = data[i]
        intlObj[langObj.field] = langObj.value
    }
    return intlObj
}

export const findLimit = (config, rate) => {
    for (const key in config) {
        const range = {
            'min': config[key]['min'],
            'max': config[key]['max'],
        }
        if (range.min <= rate && range.max >= rate) {
            return {
                'name': config[key]['name'],
                'status': key,
                'min': range.min,
                'max': range.max
            }
        }
    }
    return null
}

export function shadeColor(color, percent) {

    let R = parseInt(color.substring(1,3),16);
    let G = parseInt(color.substring(3,5),16);
    let B = parseInt(color.substring(5,7),16);

    R = parseInt(R * (100 + percent) / 100);
    G = parseInt(G * (100 + percent) / 100);
    B = parseInt(B * (100 + percent) / 100);

    R = (R<255)?R:255;
    G = (G<255)?G:255;
    B = (B<255)?B:255;

    let RR = ((R.toString(16).length===1)?"0"+R.toString(16):R.toString(16));
    let GG = ((G.toString(16).length===1)?"0"+G.toString(16):G.toString(16));
    let BB = ((B.toString(16).length===1)?"0"+B.toString(16):B.toString(16));

    return "#"+RR+GG+BB;
}

export function calculateTotalValues(results, sentiments) {
    let totalNegative = 0;
    let totalPositive = 0;
    let totalNeutral = 0;

    for (let key in results) {
        if (sentiments[key]) {
            const sentimentValue = sentiments[key].sentiment;
            if (sentimentValue > 0) {
                totalPositive += results[key];
            } else if (sentimentValue < 0) {
                totalNegative += results[key];
            } else {
                totalNeutral += results[key];
            }
        }
    }

    return { totalNegative, totalNeutral, totalPositive };
}

export function groupResultsBySentimentValue(results, sentiments) {
    const grouped = {};

    for (let key in results) {
        if (sentiments[key]) {
            const sentimentValue = sentiments[key].sentiment.toString()

            // Initialize the sentimentValue object if it does not exist
            if (!grouped[sentimentValue]) {
                grouped[sentimentValue] = { count: 0, followupresult: {} }
            }

            // Update the count
            grouped[sentimentValue].count += results[key]

            // Update the followupresult for the current key
            if (!grouped[sentimentValue].followupresult[key]) {
                grouped[sentimentValue].followupresult[key] = 0
            }
            grouped[sentimentValue].followupresult[key] += results[key]
        }
    }

    return grouped
}

export function getSentimentColor(sentimentValue, textColor) {
    if (textColor) {
        if (sentimentValue > 1 || sentimentValue < -2) {
            return "#FFFFFF"
        } else {
            return "#000000"
        }
    } else {
        if (sentimentValue > 0) {
            return shadeColor("#ABD4CC", ( 1 - sentimentValue ) * 10)
        } else if (sentimentValue < 0) {
            return shadeColor("#FEBACF", ( 1 + sentimentValue ) * 10 )
        } else {
            return shadeColor('#DFDFEC', - 10 )
        }
    }
}

function determinePrefix(dividendInfoSec, dividerInfoSec) {
    if (dividendInfoSec && !dividerInfoSec) {
        return "≤"
    } else if (!dividendInfoSec && dividerInfoSec) {
        return "≥"
    } else if (dividendInfoSec && dividerInfoSec) {
        return "≈"
    }
    return ""
}

function checkInfoSec(count) {
    return count <= 3 && count > 0
}

export function sortBySentiment(data, sentiments) {
    if (!data || Object.keys(data).length === 0) return []
    const keys = Object.keys(data)
    let keysWithSentimentValue = {}
    for (let key in keys) {
        const resultKey = keys[key]
        const results = data[resultKey]?.followupresult || null
        if (results) {
            let positives = 0
            let neutrals = 0
            let negatives = 0
            for (let result in results) {
                const count = results[result]
                const sentiment = sentiments[result]
                if (sentiment) {
                    const sentimentValue = sentiment?.sentiment
                    if (sentimentValue > 0) {
                        positives += count
                    } else if (sentimentValue < 0) {
                        negatives += count
                    } else {
                        neutrals += count
                    }
                }
            }
            const rate = (positives + neutrals) / (positives + neutrals + negatives)
            keysWithSentimentValue[resultKey] = rate
        }
    }

    const sortedKeys = keys.sort((a,b) => {
        const valueA = keysWithSentimentValue[a]
        const valueB = keysWithSentimentValue[b]

        // Sort values in decreasing fashion, then zeros, and then NaNs
        if (isNaN(valueA) && isNaN(valueB)) return 0
        if (isNaN(valueA)) return 1
        if (isNaN(valueB)) return -1

        if (valueA === 0 && valueB !== 0) return 1
        if (valueB === 0 && valueA !== 0) return -1

        return valueB - valueA
    })
    return sortedKeys
}

export function calculateFollowupRates(data, sentiments, key) {
    const supportTypes = data[key];
    let results = [];

    for (let supportType in supportTypes) {
        if (supportTypes[supportType].followupresult) {
            const followupResults = supportTypes[supportType].followupresult;
            let positiveCount = 0;
            let neutralCount = 0;
            let negativeCount = 0;
            let dividendInfoSec = false;
            let dividerInfoSec = false;

            for (let result in followupResults) {
                const count = followupResults[result]

                if (sentiments[result]) {
                    const sentimentValue = sentiments[result].sentiment;
                    if (sentimentValue > 0) {
                        positiveCount += count
                        if (checkInfoSec(count)) {
                            dividendInfoSec = true;
                        }
                    }
                    else if (sentimentValue === 0) {
                        neutralCount += count
                        if (checkInfoSec(count)) {
                            dividendInfoSec = true;
                        }
                    }
                    else {
                        negativeCount += count
                        if (checkInfoSec(count)) {
                            dividerInfoSec = true;
                        }
                    }
                }
            }

            const rate = (positiveCount + neutralCount + negativeCount) === 0 ? 0 : (positiveCount + neutralCount) / (positiveCount + neutralCount + negativeCount);
            const prefix = negativeCount === 0 || (positiveCount + neutralCount) === 0 ? '' : determinePrefix(dividendInfoSec, dividerInfoSec)
            const amount = (dividendInfoSec ? "≤" : "") + (positiveCount + neutralCount)

            results.push({
                supportType,
                rate,
                prefix,
                amount
            });
        }
    }

    results.sort((a, b) => b.rate - a.rate);
    return results
}

export function sortSupportTypesByWorstResponseRate(data, key) {
    const supportTypes = data[key];
    let results = [];

    for (let supportType in supportTypes) {
        if (supportTypes[supportType].followupresult) {
            const followupResults = supportTypes[supportType].followupresult;
            let respondedCount = 0;
            let noResponseCount = followupResults?.NORESPONSE || 0;
            let dividendInfoSec = false;
            let dividerInfoSec = false;

            for (let result in followupResults) {
                const count = followupResults[result]
                if (result === "NORESPONSE") {
                    if (checkInfoSec(count)) {
                        dividerInfoSec = true
                    }
                } else {
                    respondedCount += count
                    if (checkInfoSec(count)) {
                        dividendInfoSec = true
                        dividerInfoSec = true
                    }
                }
            }

            const rate =  respondedCount / (respondedCount + noResponseCount)
            const prefix = noResponseCount === 0 || respondedCount === 0 ? '' : determinePrefix(dividendInfoSec, dividerInfoSec)
            const amount = ((noResponseCount > 0 && noResponseCount < 4) ? "≤" : "") + noResponseCount

            results.push({
                supportType,
                rate,
                prefix,
                amount
            });
        }
    }

    results.sort((a, b) => a.rate - b.rate)
    return results
}

export function isResultText(str) {
    const pattern = /^[a-zA-Z0-9]+\.text\.(bad|ok|good)\.\d+$/
    return pattern.test(str)
}

export const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(false);

    useEffect(() => {
        const media = window.matchMedia(query);
        if (media.matches !== matches) {
            setMatches(media.matches)
        }
        const listener = () => setMatches(media.matches)
        window.addEventListener("resize", listener)
        return () => window.removeEventListener("resize", listener)
    }, [matches, query])

    return matches
}
